import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Type, Search, Loader2, X } from 'lucide-react';
import api from '../../utils/api';

// Module-level cache (shared with ContentPlaceholderPicker)
let placeholderCache = null;
let cacheLoading = false;

// ─── Fetch & cache content placeholders ─────────────────────────────────────
const fetchContentPlaceholders = async () => {
    if (placeholderCache) return placeholderCache;
    if (cacheLoading) {
        // Wait for pending fetch
        return new Promise((resolve) => {
            const check = setInterval(() => {
                if (placeholderCache) { clearInterval(check); resolve(placeholderCache); }
            }, 100);
        });
    }
    cacheLoading = true;
    try {
        const res = await api.get('/placeholders/available');
        if (res.data?.success) {
            placeholderCache = res.data.content_placeholders || [];
        } else {
            placeholderCache = [];
        }
    } catch {
        placeholderCache = [];
    } finally {
        cacheLoading = false;
    }
    return placeholderCache;
};

// ─── Placeholder Picker Dropdown (inline version) ───────────────────────────
const InlinePlaceholderDropdown = ({ onSelect, onClose, position, replacing, initialSearch = '' }) => {
    const [placeholders, setPlaceholders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState(initialSearch);
    const dropdownRef = useRef(null);
    const searchRef = useRef(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            const data = await fetchContentPlaceholders();
            if (mounted) { setPlaceholders(data); setLoading(false); }
        })();
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        searchRef.current?.focus();
    }, []);

    // Sync search query from autosuggest typing
    useEffect(() => {
        if (initialSearch !== undefined) {
            setSearchQuery(initialSearch);
        }
    }, [initialSearch]);

    // Close on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [onClose]);

    const filtered = useMemo(() =>
        placeholders.filter(p =>
            p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.slug.toLowerCase().includes(searchQuery.toLowerCase())
        ), [placeholders, searchQuery]
    );

    return (
        <div
            ref={dropdownRef}
            className="absolute z-50 w-64 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden text-left"
            style={{ top: position.top, left: position.left }}
        >
            {/* Header */}
            <div className="px-2.5 py-2 border-b border-gray-100 bg-gray-50">
                {replacing && (
                    <p className="text-[10px] text-violet-600 font-semibold mb-1.5 uppercase tracking-wide">
                        Replace: {replacing}
                    </p>
                )}
                <div className="relative">
                    <Search className="absolute left-2 top-1.5 w-3.5 h-3.5 text-gray-400" />
                    <input
                        ref={searchRef}
                        type="text"
                        placeholder="Search placeholders..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-violet-500 bg-white"
                    />
                </div>
            </div>

            {/* List */}
            <div className="max-h-52 overflow-y-auto p-1 py-1.5">
                {loading ? (
                    <div className="flex items-center justify-center p-4 text-gray-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-3 text-center text-xs text-gray-400">
                        No placeholders found
                    </div>
                ) : (
                    <div className="space-y-0.5">
                        {filtered.map(p => (
                            <button
                                key={p.slug}
                                type="button"
                                onClick={() => onSelect(`{${p.slug}}`)}
                                className="w-full text-left px-2 py-1.5 hover:bg-violet-50 rounded group transition-colors"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs font-semibold text-gray-700 group-hover:text-violet-700">
                                        {p.name}
                                    </span>
                                    <span className="text-[10px] font-mono text-gray-400 bg-gray-100 px-1 rounded group-hover:bg-violet-100 group-hover:text-violet-600">
                                        {'{' + p.slug + '}'}
                                    </span>
                                </div>
                                {p.description && (
                                    <p className="text-[10px] text-gray-500 truncate mt-0.5" title={p.description}>
                                        {p.description}
                                    </p>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Regex to find placeholders ─────────────────────────────────────────────
const PLACEHOLDER_REGEX = /\{([a-zA-Z0-9_.]+)\}/g;

// ═══════════════════════════════════════════════════════════════════════════════
// PlaceholderTextarea
//
// A textarea-like component that:
// 1. Highlights placeholders like {employee.first_name} with a colored chip look
// 2. Clicking a placeholder opens a picker to replace it
// 3. Insert Data inserts at cursor position, not at end
// ═══════════════════════════════════════════════════════════════════════════════
const PlaceholderTextarea = ({
    value = '',
    onChange,
    rows = 3,
    placeholder = '',
    className = '',
    focusRingColor = 'focus:ring-violet-500'
}) => {
    const textareaRef = useRef(null);
    const containerRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    const [dropdown, setDropdown] = useState(null); // { mode: 'insert' | 'replace' | 'autosuggest', position, replacingText, replaceStart, replaceEnd, anchorPos }
    const [cursorPos, setCursorPos] = useState(null);

    // Track cursor so we can insert at position
    const handleSelect = useCallback(() => {
        if (textareaRef.current) {
            setCursorPos(textareaRef.current.selectionStart);
        }
    }, []);

    // ── Insert at cursor ─────────────────────────────────────────────────
    const handleInsertAtCursor = useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const rect = containerRef.current.getBoundingClientRect();
        setDropdown({
            mode: 'insert',
            position: { top: '100%', left: '0px' },
            replacingText: null,
            replaceStart: null,
            replaceEnd: null
        });
    }, []);

    // ── Handle selecting a placeholder from dropdown ──────────────────────
    const handleDropdownSelect = useCallback((newPlaceholder) => {
        const textarea = textareaRef.current;
        if (!textarea) { setDropdown(null); return; }

        if (dropdown.mode === 'replace') {
            // Replace the clicked placeholder
            const before = value.substring(0, dropdown.replaceStart);
            const after = value.substring(dropdown.replaceEnd);
            const newValue = before + newPlaceholder + after;
            onChange({ target: { value: newValue } });

            setTimeout(() => {
                textarea.focus();
                const newPos = dropdown.replaceStart + newPlaceholder.length;
                textarea.setSelectionRange(newPos, newPos);
            }, 0);
        } else if (dropdown.mode === 'autosuggest') {
            // Replace the partial "{text" typed so far with the full placeholder
            const anchorPos = dropdown.anchorPos;
            const currentPos = textarea.selectionStart;
            const before = value.substring(0, anchorPos);
            const after = value.substring(currentPos);
            const newValue = before + newPlaceholder + after;
            onChange({ target: { value: newValue } });

            setTimeout(() => {
                textarea.focus();
                const newPos = anchorPos + newPlaceholder.length;
                textarea.setSelectionRange(newPos, newPos);
            }, 0);
        } else {
            // Insert at cursor position
            const pos = cursorPos ?? value.length;
            const before = value.substring(0, pos);
            const after = value.substring(pos);
            const newValue = before + newPlaceholder + after;
            onChange({ target: { value: newValue } });

            setTimeout(() => {
                textarea.focus();
                const newPos = pos + newPlaceholder.length;
                textarea.setSelectionRange(newPos, newPos);
            }, 0);
        }

        setDropdown(null);
    }, [dropdown, value, onChange, cursorPos]);

    // ── Click in textarea: check if a placeholder was clicked ─────────────
    const handleClick = useCallback((e) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const clickPos = textarea.selectionStart;

        // Find all placeholders in text
        let match;
        PLACEHOLDER_REGEX.lastIndex = 0;
        while ((match = PLACEHOLDER_REGEX.exec(value)) !== null) {
            const start = match.index;
            const end = match.index + match[0].length;
            if (clickPos >= start && clickPos <= end) {
                // User clicked inside a placeholder!
                e.preventDefault();

                // Highlight the placeholder text
                textarea.setSelectionRange(start, end);

                // Calculate approximate position for dropdown
                setDropdown({
                    mode: 'replace',
                    position: { top: '100%', left: '0px' },
                    replacingText: match[0],
                    replaceStart: start,
                    replaceEnd: end
                });
                return;
            }
        }
    }, [value]);

    // Handle Escape to close dropdown, and Backspace/Delete to remove whole placeholders
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape' && dropdown) {
            e.preventDefault();
            setDropdown(null);
            return;
        }

        // Close autosuggest if user types } (they completed manually)
        if (e.key === '}' && dropdown?.mode === 'autosuggest') {
            setDropdown(null);
            return;
        }

        // Backspace or Delete: remove entire placeholder at once
        if (e.key === 'Backspace' || e.key === 'Delete') {
            const textarea = textareaRef.current;
            if (!textarea) return;

            // If autosuggest is open and backspace would delete the { anchor, close it
            if (dropdown?.mode === 'autosuggest' && e.key === 'Backspace') {
                const pos = textarea.selectionStart;
                if (pos <= dropdown.anchorPos + 1) {
                    setDropdown(null);
                    // Let the default backspace delete the { character
                    return;
                }
            }

            const pos = textarea.selectionStart;
            const selEnd = textarea.selectionEnd;

            // If there's a text selection, let default behavior handle it
            if (pos !== selEnd) return;

            // Skip placeholder-atomic-delete if autosuggest is open (user is typing a partial)
            if (dropdown?.mode === 'autosuggest') return;

            // Find all placeholders and check if cursor is inside or adjacent
            let match;
            PLACEHOLDER_REGEX.lastIndex = 0;
            while ((match = PLACEHOLDER_REGEX.exec(value)) !== null) {
                const phStart = match.index;
                const phEnd = match.index + match[0].length;

                let shouldDelete = false;

                if (e.key === 'Backspace') {
                    if (pos > phStart && pos <= phEnd) {
                        shouldDelete = true;
                    }
                } else {
                    if (pos >= phStart && pos < phEnd) {
                        shouldDelete = true;
                    }
                }

                if (shouldDelete) {
                    e.preventDefault();
                    const before = value.substring(0, phStart);
                    const after = value.substring(phEnd);
                    onChange({ target: { value: before + after } });

                    setTimeout(() => {
                        textarea.focus();
                        textarea.setSelectionRange(phStart, phStart);
                    }, 0);
                    return;
                }
            }
        }
    }, [dropdown, value, onChange]);

    // ── Auto-suggest: detect { typing and update search as user types ─────
    const handleChange = useCallback((e) => {
        const newValue = e.target.value;
        const newPos = e.target.selectionStart;
        onChange(e);
        setCursorPos(newPos);

        // Check if user just typed a { character
        if (newValue.length > value.length) {
            const typedChar = newValue[newPos - 1];
            if (typedChar === '{' && !dropdown) {
                // Open autosuggest
                setDropdown({
                    mode: 'autosuggest',
                    position: { top: '100%', left: '0px' },
                    replacingText: null,
                    replaceStart: null,
                    replaceEnd: null,
                    anchorPos: newPos - 1 // position of the { character
                });
                return;
            }
        }

        // If autosuggest is open, update the search query based on text after {
        if (dropdown?.mode === 'autosuggest') {
            const textAfterBracket = newValue.substring(dropdown.anchorPos + 1, newPos);
            // Close if user moved cursor before the anchor or typed a space/newline
            if (newPos <= dropdown.anchorPos || /[\s}]/.test(textAfterBracket)) {
                setDropdown(null);
            } else {
                // Update dropdown with current partial text for filtering
                setDropdown(prev => prev ? { ...prev, _searchHint: textAfterBracket } : null);
            }
        }
    }, [value, onChange, dropdown]);

    // ── Render highlighted overlay (for visual placeholder highlighting) ──
    const renderHighlightedOverlay = useMemo(() => {
        if (!value) return null;

        const parts = [];
        let lastIndex = 0;

        PLACEHOLDER_REGEX.lastIndex = 0;
        let match;
        while ((match = PLACEHOLDER_REGEX.exec(value)) !== null) {
            // Text before placeholder
            if (match.index > lastIndex) {
                parts.push(
                    <span key={`text-${lastIndex}`} className="invisible">
                        {value.substring(lastIndex, match.index)}
                    </span>
                );
            }
            // The placeholder itself — styled chip
            parts.push(
                <span
                    key={`ph-${match.index}`}
                    className="relative inline bg-violet-100 text-violet-700 rounded px-0.5 border border-violet-200 cursor-pointer font-medium"
                    style={{ fontSize: 'inherit', lineHeight: 'inherit' }}
                >
                    {match[0]}
                </span>
            );
            lastIndex = match.index + match[0].length;
        }
        // Remaining text
        if (lastIndex < value.length) {
            parts.push(
                <span key={`text-${lastIndex}`} className="invisible">
                    {value.substring(lastIndex)}
                </span>
            );
        }

        return parts.length > 0 ? parts : null;
    }, [value]);

    return (
        <div className="relative" ref={containerRef}>
            {/* Header with Insert Data button */}
            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                    {value && (() => {
                        PLACEHOLDER_REGEX.lastIndex = 0;
                        const count = (value.match(PLACEHOLDER_REGEX) || []).length;
                        return count > 0 ? (
                            <span className="text-[10px] text-violet-500 font-medium">
                                {count} placeholder{count > 1 ? 's' : ''} · click to replace
                            </span>
                        ) : null;
                    })()}
                </div>
                <button
                    type="button"
                    onClick={handleInsertAtCursor}
                    className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-violet-600 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded transition-colors"
                    title="Insert Employee Data Placeholder at cursor position"
                >
                    <Type className="w-3.5 h-3.5" />
                    <span>Insert Data</span>
                </button>
            </div>

            {/* Textarea (actual editable element) */}
            <div className="relative">
                <textarea
                    ref={textareaRef}
                    value={value}
                    onChange={handleChange}
                    onSelect={handleSelect}
                    onClick={handleClick}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    rows={rows}
                    placeholder={placeholder}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm ${focusRingColor} focus:ring-2 focus:border-transparent resize-none ${className}`}
                    style={{ caretColor: 'auto', background: 'white' }}
                />
            </div>

            {/* Dropdown */}
            {dropdown && (
                <InlinePlaceholderDropdown
                    onSelect={handleDropdownSelect}
                    onClose={() => setDropdown(null)}
                    position={dropdown.position}
                    replacing={dropdown.replacingText}
                    initialSearch={dropdown._searchHint || ''}
                />
            )}
        </div>
    );
};

export default PlaceholderTextarea;
